import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import Markdown from 'react-markdown';
import { useSearchParams } from 'react-router';
import remarkGfm from 'remark-gfm';

import type { LabReportSummaryDto } from '../../../shared/api.js';
import { api } from '../lib/api';

type Kind = LabReportSummaryDto['kind'];

/** Tab names in tab order; every kind must be here (the type checks it). */
const KIND_LABELS: Record<Kind, string> = {
  blood_panel: 'Кровь',
  urine: 'Моча',
  microbiome: 'Микробиом',
  parasitology: 'Паразитология',
  spermiogram: 'Спермограмма',
  genetics: 'Генетика',
  other: 'Другое',
};
const KINDS = Object.keys(KIND_LABELS) as Kind[];

function formatDate(isoDate: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${isoDate}T12:00:00Z`));
}

function label(r: LabReportSummaryDto): string {
  const flagged = r.outOfRangeCount > 0 ? ` · вне нормы: ${r.outOfRangeCount}` : '';
  return `${formatDate(r.date)} — ${r.lab}${flagged}`;
}

/**
 * Lab reports by kind (blood, microbiome…), one tab each; within a tab newest first, the latest
 * open. The tab is in the address (`?kind=`). Reads are logged (medical data).
 */
export function Labs() {
  const list = useQuery({ queryKey: ['labs', 'reports'], queryFn: api.labReports });
  const [params, setParams] = useSearchParams();
  const [chosen, setChosen] = useState<string | undefined>();
  const all = list.data?.reports ?? [];
  const present = KINDS.filter((k) => all.some((r) => r.kind === k));
  const asked = params.get('kind');
  const kind = present.find((k) => k === asked) ?? present[0];
  const reports = all.filter((r) => r.kind === kind);
  const id = reports.find((r) => r.id === chosen)?.id ?? reports[0]?.id;
  const report = useQuery({
    queryKey: ['labs', 'report', id],
    queryFn: () => api.labReport(id ?? ''),
    enabled: Boolean(id),
  });

  const pickKind = (k: Kind) => {
    setChosen(undefined);
    setParams(k === present[0] ? {} : { kind: k }, { replace: true });
  };

  return (
    <div className="page">
      <div className="page__header">
        <h2>Анализы</h2>
        {reports.length > 1 && (
          <select value={id} onChange={(e) => setChosen(e.target.value)} aria-label="Отчёт">
            {reports.map((r) => (
              <option key={r.id} value={r.id}>
                {label(r)}
              </option>
            ))}
          </select>
        )}
      </div>
      {present.length > 1 && (
        <div className="tabs" role="tablist" aria-label="Виды анализов">
          {present.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={k === kind}
              className={k === kind ? 'active' : undefined}
              onClick={() => pickKind(k)}
            >
              {KIND_LABELS[k]}
              <span className="tabs__count">{all.filter((r) => r.kind === k).length}</span>
            </button>
          ))}
        </div>
      )}
      {list.isPending && <p className="muted">Загрузка…</p>}
      {list.isError && <p className="text-error">{list.error.message}</p>}
      {list.data && !list.data.available && <p className="muted">Анализы недоступны: нет базы.</p>}
      {list.data?.available && all.length === 0 && <p className="muted">Отчётов пока нет.</p>}
      {report.isError && <p className="text-error">{report.error.message}</p>}
      {report.data && (
        <article className="card lab-report">
          <p className="muted small">
            {reports.length === 1 && `${label(report.data.report)} · `}
            {report.data.report.resultCount > 0
              ? `${report.data.report.resultCount} показателей`
              : 'текстовый отчёт'}
            {report.data.report.outOfRangeCount > 0 &&
              ` · вне референса: ${report.data.report.outOfRangeCount}`}
            {' · '}референсы — лаборатории, выдавшей отчёт
          </p>
          <div className="markdown">
            <Markdown remarkPlugins={[remarkGfm]}>{report.data.markdown}</Markdown>
          </div>
        </article>
      )}
    </div>
  );
}
